
import React, {useEffect, useState} from 'react';
import {array, arrayOf, bool, element, func, number, object, shape, string} from 'prop-types';
import {cloneDeep, isEmpty, isUndefined} from 'lodash';
import {BRUSH_STROKES} from '../chart-colors.js';
import {
    ResponsiveContainer,
    ComposedChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Brush
} from 'recharts';
import {DateUtil} from '../../../utils/date-util.js';
import {getLineChartConfig} from './line-chart-wrapper-util.js';

const LineChartWrapper = ({configChart, chartData}) => {

    const [data, setData] = useState(chartData);

    const {
        chartWidth,
        chartHeight,
        plotLines,
        getLineKeys,
        getGroupLineKeys,
        lineWidth,
        lineType,
        lineColors,
        isDashedLine,
        strokeDashArray,
        isAnimation,
        xAxisKey,
        xAxisPaddingLeft,
        xAxisPaddingRight,
        xAxisTickFormatter,
        yAxisTickFormatter,
        legendLabels,
        legendIcon,
        legendTextColor,
        isGroupLegend,
        isToggleChartOnClickOfLegend,
        isVisibleHorizontalGrid,
        tooltipContent,
        brush
    } = getLineChartConfig(configChart, data || []);

    const [displayedLineKeys, setDisplayedLineKeys] = useState(isGroupLegend ? getGroupLineKeys(plotLines) : getLineKeys(plotLines));

    const renderColorfulLegendText = (legendLabel, entry) => {
        const labelText = legendLabels ? legendLabels[entry.payload.dataKey] : legendLabel;
        const legendFontColor = legendTextColor === '' ? entry.color : legendTextColor;
        return <span style={{ color: legendFontColor }}>{labelText}</span>;
    };

    const handleOnClickLegendItem = legendItem => {
        const compareKey = isGroupLegend ? legendItem.groupKey : legendItem.dataKey;
        const foundAt = displayedLineKeys.indexOf(compareKey);
        const copyOfDataKeys = cloneDeep(displayedLineKeys);
        foundAt < 0 ? copyOfDataKeys.push(compareKey) : copyOfDataKeys.splice(foundAt, 1);
        setDisplayedLineKeys(copyOfDataKeys);
    };

    const renderLegend = props => {
        const groupKeys = [];
        const { payload } = props;

        return <div className='custom-legend-container'>
            {payload.map((entry, index) => {
            if(groupKeys.indexOf(entry.payload.groupKey) === -1) {
                groupKeys.push(entry.payload.groupKey);
                const onClickHandler = () => handleOnClickLegendItem(entry.payload);
                const isOmit = displayedLineKeys.indexOf(entry.payload.groupKey) === -1;
                const omittedCss = isOmit ? ' custom-legend-list--removed' : '';
                return <>
                    <span onClick={onClickHandler} className='custom-legend-list--icon' style={{backgroundColor: isOmit? '#cccccc' : entry.payload.stroke}} key={`item-icon-${index}`}></span>
                    <span onClick={onClickHandler} className={`custom-legend-list--text${omittedCss}`} key={`item-text-${index}`}>{entry.payload.groupKey}</span>
                </>;
            } else {
                return null;
            }
            })}
        </div>;
    };

    const isShowLegend = (data, plotLines) => !(isEmpty(data) || isEmpty(plotLines));

    useEffect(() => {
        setData(chartData);
    }, [chartData]);

    useEffect(() => {
        setDisplayedLineKeys(isGroupLegend ? getGroupLineKeys(plotLines) : getLineKeys(plotLines));
    }, [plotLines]);

    return (isEmpty(data) || isEmpty(plotLines)) ? <div style={{height: '20rem'}}><h4 className='no-records-found'>No Records Found</h4></div> :
        <ResponsiveContainer
            width={chartWidth}
            height={chartHeight ? chartHeight : 400}
            debounce={500}
            key={DateUtil.getNowMoment()} //TODO: to implement in a better way
        >
            <ComposedChart
                data={data}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
                {isVisibleHorizontalGrid && <CartesianGrid vertical={false} />}

                {!isEmpty(data) && <XAxis
                    xAxisId={0}
                    padding={{
                        left: xAxisPaddingLeft,
                        right: xAxisPaddingRight
                    }}
                    dataKey={xAxisKey}
                    tickFormatter={xAxisTickFormatter}
                />}

                {!isEmpty(data) && <YAxis tickFormatter={yAxisTickFormatter} />}

                {tooltipContent ? <Tooltip content={tooltipContent} /> : <Tooltip />}

                {isShowLegend(data, plotLines) ? (isGroupLegend ? <Legend content={renderLegend} /> : <Legend
                    wrapperStyle={{
                        paddingTop: '10px'
                    }}
                    iconType={legendIcon}
                    formatter={renderColorfulLegendText}
                    onClick={isToggleChartOnClickOfLegend ? handleOnClickLegendItem : () => {}}
                />) : null}

                {!isEmpty(data) &&
                    plotLines.map((lineDetails, lineIndex) => {
                        const sWidth = isUndefined(lineDetails.lineWidth) ? lineWidth : lineDetails.lineWidth;
                        const lineDesign = isEmpty(lineDetails.lineType) ? lineType : lineDetails.lineType;
                        const strokeColor = isEmpty(lineDetails.lineColor) ? lineColors[lineIndex] : lineDetails.lineColor;
                        let additionalProps = lineDetails.isDashedLine || isDashedLine ?
                            {strokeDasharray: (lineDetails.strokeDashArray || strokeDashArray)} : {};
                        additionalProps = lineDetails.groupKey ?
                            {...additionalProps, groupKey: lineDetails.groupKey} : {...additionalProps};
                        const matchKey = isGroupLegend ? lineDetails.groupKey : lineDetails.lineKey;

                        return <Line
                            hide={displayedLineKeys.indexOf(matchKey) < 0}
                            groupKey={lineDetails.groupKey}
                            dataKey={lineDetails.lineKey}
                            type={lineDesign}
                            stroke={strokeColor}
                            strokeWidth={sWidth}
                            activeDot={{r: (sWidth + 2)}}
                            isAnimationActive={isAnimation}
                            {...additionalProps}
                        />
                    })
                }

                {!isEmpty(data) && !isEmpty(brush) && (
                    <Brush
                        height={20}
                        dataKey={xAxisKey}
                        startIndex={brush.startIndex}
                        endIndex={brush.endIndex}
                        stroke={BRUSH_STROKES[0]}
                        tickFormatter={brush.tickFormatter}
                    />
                )}

            </ComposedChart>
        </ResponsiveContainer>;
};

LineChartWrapper.propTypes = {
    chartData: array.isRequired,
    configChart: shape({
        chartTitle: string.isRequired,
        chartWidth: number,
        chartHeight: number,
        configLines: shape({
            plotLines: arrayOf(string).isRequired,
            isHighLightLineOnClick: bool,
            isHighLightCellOnClick: bool,
            isStackedLines: bool,
            lineWidth: number,
            lineColors: object,
            isLineRoundedEdge: bool,
            isSetLineBackground: bool,
            lineBackgroundColor: string,
            lineOnClickHandler: func
        }).isRequired,
        configXAxis: shape({
            xAxisKey: string.isRequired,
            xAxisTickFormatter: func
        }).isRequired,
        configYAxis: shape({
            yAxisTickFormatter: func
        }),
        chartFilters: element,
        configToggle: shape({
            toggleName: string.isRequired,
            className: string,
            toggle: arrayOf(
                shape({
                    toggleLabel: string.isRequired,
                    toggleValue: string.isRequired,
                    className: string
                })).isRequired
        }.isRequired),
        configLegends: shape({
            legendLabels: object,
            isToggleChartOnClickOfLegend: bool,
            legendIcon: string,
            isLegendTextColorSameAsChartColor: bool
        }),
        isVisibleHorizontalGrid: bool,
        tooltipContent: element
    }),
    apiErrorMessage: object,
    toolbarConfig: shape({
        isShowToolbar: bool,
        refreshIconOnClickHandler: func,
        lineChartIconOnClickHandler: func,
        gridChartIconOnClickHandler: func
    })
};

export default LineChartWrapper;
